import React, { useRef, useState } from "react";
import { Play, Tv } from "lucide-react";
import { INTERVIEWS } from "../lib/content";

const InterviewCard = ({ item, i }) => {
  const [active, setActive] = useState(false);
  const videoRef = useRef(null);

  const play = () => {
    setActive(true);
    requestAnimationFrame(() => videoRef.current?.play());
  };

  return (
    <article
      data-testid={`interview-card-${i}`}
      className="reveal group relative rounded-3xl overflow-hidden"
      style={{ transitionDelay: `${i * 80}ms`, background: "#1C201E", border: "1px solid #2A2F2C" }}
    >
      <div className="relative aspect-video overflow-hidden bg-black">
        {active ? (
          <video
            ref={videoRef}
            data-testid={`interview-video-${i}`}
            src={item.src}
            poster={item.poster}
            controls
            playsInline
            preload="metadata"
            className="absolute inset-0 w-full h-full object-contain bg-black"
          />
        ) : (
          <button
            type="button"
            onClick={play}
            data-testid={`interview-play-${i}`}
            aria-label={`Play interview: ${item.title}`}
            className="absolute inset-0 w-full h-full"
          >
            <img
              src={item.poster}
              alt={item.title}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <span className="absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/20 to-transparent" aria-hidden />
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="relative inline-flex items-center justify-center w-16 h-16 rounded-full bg-terracotta text-ink shadow-[0_12px_40px_-8px_rgba(245,187,31,0.7)] transition-transform duration-300 group-hover:scale-110">
                <span className="absolute inset-0 rounded-full bg-terracotta animate-ping opacity-30" aria-hidden />
                <Play size={26} strokeWidth={2.4} className="ml-1" fill="currentColor" />
              </span>
            </span>
          </button>
        )}
      </div>

      <div className="p-6 flex items-start gap-4">
        <span className="mt-0.5 inline-flex items-center justify-center w-10 h-10 rounded-xl bg-terracotta/15 text-terracotta-soft shrink-0">
          <Tv size={18} strokeWidth={2} />
        </span>
        <div>
          <div className="eyebrow !text-terracotta-soft !text-[10px]">{item.outlet}</div>
          <h3 className="font-display mt-1.5 text-lg font-bold text-alabaster leading-snug">
            {item.title}
          </h3>
        </div>
      </div>
    </article>
  );
};

const Interviews = () => (
  <section
    id="interviews"
    data-testid="section-interviews"
    className="relative py-16 sm:py-20 bg-ink text-alabaster overflow-hidden"
  >
    <div className="absolute inset-0 dot-grid opacity-[0.08] pointer-events-none" aria-hidden />
    <div
      className="absolute -top-40 -left-40 w-[480px] h-[480px] rounded-full pointer-events-none"
      style={{ background: "radial-gradient(circle, rgba(245,187,31,0.18), transparent 60%)" }}
      aria-hidden
    />

    <div className="relative max-w-7xl mx-auto px-6 sm:px-10">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12 items-end">
        <div className="lg:col-span-7 reveal">
          <div className="eyebrow !text-terracotta-soft inline-flex items-center gap-3">
            <span className="block w-10 h-px bg-terracotta-soft" />
            On Air
          </div>
          <h2
            data-testid="interviews-heading"
            className="font-display mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold text-alabaster leading-[1.05] tracking-tight"
          >
            Media & <span className="text-terracotta-soft">TV interviews.</span>
          </h2>
        </div>
        <div className="lg:col-span-5 reveal">
          <p className="text-alabaster/70 text-base sm:text-lg leading-relaxed">
            Featured on television discussing my innovations, patents, and AI projects — and the mission to turn research into tools that impact the world.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
        {INTERVIEWS.map((item, i) => (
          <InterviewCard key={item.src} item={item} i={i} />
        ))}
      </div>
    </div>
  </section>
);

export default Interviews;
